import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch } from "utils/adminFetch";

function StatusBadge({ status }) {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "confirmed") {
    return (
      <span className="inline-flex items-center rounded-md bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700 dark:bg-green-500/15 dark:text-green-300">
        Approved
      </span>
    );
  }
  if (s === "waiting_for_approval" || s === "awaiting_approval") {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
        Waiting for approval
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex items-center rounded-md bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 dark:bg-red-500/15 dark:text-red-300">
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 dark:bg-white/10 dark:text-gray-300">
      {s || "Pending"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function SlotVerificationAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("waiting_for_approval");
  const [actionId, setActionId] = useState(null);
  const [msg, setMsg] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/slot-verifications");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load slot payments.");
        setRows([]);
        return;
      }
      setRows(data.rows || []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter(
      (r) => String(r.status || "").toLowerCase() === filter,
    );
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = {
      waiting_for_approval: 0,
      approved: 0,
      pending: 0,
      failed: 0,
    };
    for (const r of rows) {
      const s = String(r.status || "").toLowerCase();
      if (c[s] != null) c[s] += 1;
    }
    return c;
  }, [rows]);

  async function handleApprove(r) {
    if (!r?.id || actionId) return;
    setActionId(r.id);
    setError("");
    setMsg("");
    try {
      const res = await adminFetch(
        `/api/admin/slot-verifications/${r.id}/approve`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Approve failed.");
        return;
      }
      setMsg(
        data.message ||
          `Approved slot ${r.slotId || r.transactionId || r.id}.`,
      );
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="mt-3 px-2">
      <h4 className="mb-2 text-2xl font-bold text-navy-700 dark:text-white">
        Slot verification
      </h4>
      <p className="mb-6 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
        When a client pays via Plisio, the payment appears here as{" "}
        <strong>Waiting for approval</strong>. Open the Plisio transaction to
        verify the amount, then click <strong>Approve</strong> to complete the
        operation on Plisio and unlock the slot for the client.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[
          {
            id: "waiting_for_approval",
            label: `Waiting for approval (${counts.waiting_for_approval})`,
          },
          { id: "approved", label: `Approved (${counts.approved})` },
          { id: "pending", label: `Awaiting payment (${counts.pending})` },
          { id: "failed", label: `Failed (${counts.failed})` },
          { id: "all", label: `All (${rows.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setFilter(tab.id)}
            className={
              filter === tab.id
                ? "rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white shadow"
                : "rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            }
          >
            {tab.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void loadRows()}
          disabled={loading}
          className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p className="mb-3 text-sm font-medium text-red-500">{error}</p>
      ) : null}
      {msg ? (
        <p className="mb-3 text-sm font-medium text-green-600 dark:text-green-300">
          {msg}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10">
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Client ID
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Slot ID
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Transaction ID (Plisio)
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Status
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Received
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Updated
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  No slot payments in this filter. If you see a schema error,
                  run{" "}
                  <code className="text-xs">
                    backend/sql/paid_slots_admin_verification.sql
                  </code>{" "}
                  in Supabase.
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-gray-100 align-top dark:border-white/5"
                >
                  <td className="px-4 py-3 text-navy-700 dark:text-white">
                    <div className="font-mono font-medium">{r.clientId}</div>
                    {r.clientEmail ? (
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {r.clientEmail}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-navy-700 dark:text-white">
                    {r.slotId || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.transactionId && r.plisioUrl ? (
                      <a
                        href={r.plisioUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm font-semibold text-brand-500 underline-offset-2 hover:underline"
                      >
                        {r.transactionId}
                      </a>
                    ) : (
                      <span className="font-mono text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-navy-700 dark:text-white">
                    {r.actuallyPaid != null ? `${r.actuallyPaid} USDT` : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {formatDate(r.updatedAt || r.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    {r.status === "waiting_for_approval" ? (
                      <button
                        type="button"
                        disabled={actionId === r.id}
                        onClick={() => void handleApprove(r)}
                        className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {actionId === r.id ? "Approving…" : "Approve"}
                      </button>
                    ) : r.status === "approved" && r.approvedBy ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        by {r.approvedBy}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
